import React, { useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import axios from "axios";

const baseUrl = process.env.REACT_APP_SERVER_URL;
Chart.register(...registerables);

const CompararConReport = () => {
  const [selectedYears, setSelectedYears] = useState(["2024", "2025"]);
  const [chartType, setChartType] = useState("Columnas apiladas");
  const [chartData, setChartData] = useState(null);
  const [allPrinciplesData, setAllPrinciplesData] = useState([]);

  // Fetch static principle metadata
  useEffect(() => {
    const fetchPrinciples = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/principal/`, {
          headers: {
            Authorization: `Token ${localStorage.getItem("auth_token")}`,
          },
        });
        setAllPrinciplesData(response.data?.data || []);
      } catch (error) {
        console.error("Error fetching principle metadata:", error);
      }
    };

    fetchPrinciples();
  }, []);

  // Fetch report data based on years
  const fetchData = async (years) => {
    if (!years || years.length === 0 || allPrinciplesData.length === 0) return;

    const token = localStorage.getItem("auth_token");
    const queryString = years.map((year) => `year=${year}`).join("&");
    const url = `${baseUrl}/api/principal/reports/graph_results?${queryString}`;

    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: `Token ${token}`,
        },
      });

      const apiData = response.data.data;

      // Sort and map principles for consistent labels
      const sortedPrinciples = [...allPrinciplesData].sort(
        (a, b) => a.sorting_index - b.sorting_index
      );

      const labels = sortedPrinciples.map(
        (item, index) => `Principio ${index + 1}: ${item.title}`
      );

      // Initialize weights
      const weightResults = {};
      years.forEach((year) => (weightResults[year] = []));

      // Populate weights by matching principle titles
      sortedPrinciples.forEach((principle) => {
        const matchingCategory = apiData.find(
          (item) =>
            item.title.trim().toLowerCase() ===
            principle.title.trim().toLowerCase()
        );

        years.forEach((year) => {
          weightResults[year].push(
            matchingCategory?.results?.[year]?.weight_result || 0
          );
        });
      });

      // Prepare chart datasets
      const datasets = years.map((year, i) => ({
        label: year,
        data: weightResults[year],
        backgroundColor: i === 0 ? "#2B327F" : "#E00918",
      }));

      setChartData({ labels, datasets });
    } catch (error) {
      console.error("Error fetching chart data:", error);
    }
  };

  // Trigger report data fetch when selectedYears or metadata changes
  useEffect(() => {
    if (selectedYears.length > 0 && allPrinciplesData.length > 0) {
      fetchData(selectedYears);
    }
  }, [selectedYears, allPrinciplesData]);

  const handleYearChange = (event) => {
    const years = event.target.value.split(" - ").map((y) => y.trim());
    setSelectedYears(years);
  };

  const getChartOptions = () => ({
    responsive: true,
    plugins: { legend: { position: "top" } },
    scales: {
      x: {
        stacked: chartType === "Columnas apiladas",
        ticks: {
          autoSkip: false,
          font: { size: 10 },
          maxRotation: 0,
          minRotation: 0,
          callback: function (value) {
            const label = this.getLabelForValue(value);
            const parts = label.split(": ");
            if (parts.length === 2) {
              const principle = parts[0] + ":";
              const name = parts[1];
              const words = name.split(" ");
              let lines = [];
              let currentLine = "";
              words.forEach((word) => {
                if ((currentLine + " " + word).length > 15) {
                  lines.push(currentLine);
                  currentLine = word;
                } else {
                  currentLine += (currentLine ? " " : "") + word;
                }
              });
              if (currentLine) lines.push(currentLine);
              return [principle, ...lines];
            }
            return label;
          },
        },
      },
      y: {
        stacked: chartType === "Columnas apiladas",
        beginAtZero: true,
      },
    },
  });

  const downloadChart = () => {
    const canvas = document.getElementById("compararChart");
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = "comparar-grafica.png";
    link.click();
  };

  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
      <div style={filterContainerStyle}>
        <div>
          <label style={{ marginRight: "10px" }}>Comparar con:</label>
          <select
            style={selectStyle}
            onChange={handleYearChange}
            value={selectedYears.join(" - ")}
          >
            <option>2022 - 2023</option>
            <option>2023 - 2024</option>
            <option>2024 - 2025</option>
          </select>
        </div>
        <div>
          <label style={{ marginLeft: "20px", marginRight: "10px" }}>
            Tipo de gráfico:
          </label>
          <select
            style={selectStyle}
            value={chartType}
            onChange={(e) => setChartType(e.target.value)}
          >
            <option>Columnas apiladas</option>
            <option>Columnas</option>
          </select>
        </div>
      </div>

      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: "25px", fontWeight: "bold" }}>
          {selectedYears.join(" - ")}
        </div>
        {chartData ? (
          <Bar
            id="compararChart"
            data={chartData}
            options={getChartOptions()}
          />
        ) : (
          <p>Cargando datos...</p>
        )}
      </div>

      <div style={{ marginTop: "20px", textAlign: "-webkit-right" }}>
        <button onClick={downloadChart} style={buttonStyle}>
          Descargar gráfica
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#007bff"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Styles
const selectStyle = {
  padding: "5px",
  marginRight: "10px",
  border: "1px solid #ddd",
  borderRadius: "4px",
  backgroundColor: "#fff",
};

const buttonStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  backgroundColor: "#fff",
  color: "#007bff",
  padding: "5px 10px",
  border: "1px solid",
  borderRadius: "5px",
  cursor: "pointer",
  fontSize: "14px",
};

const filterContainerStyle = {
  display: "flex",
  alignItems: "center",
  marginBottom: "15px",
  justifyContent: "space-between",
};

export default CompararConReport;
